
import {
  unref,
  useContext
} from '@nuxtjs/composition-api';

import useEnvironment from '../various/useEnvironment';
import useTlmCart from '../useTlmCart';
import useCategories from '../useCategories';
import useCartScenarios from '../useCartScenarios';
import useSearch from '@/composable/useSearch';
import useCheckout from '@/composable/checkout/useCheckout';
import useFlow from '@/composable/checkout/config/useFlow';
import { CHECKOUT_FLOW } from '~/constants/CheckoutFlow';
import useShoppingList from '~/composable/useShoppingList';
import {
  getSpecificCategorySlug,
  resolvePrimarySiteSection,
  resolveCheckoutTypeFromCart
} from '~/composable/tracking/captureReceiptTrackingSnapshot'

/**
 * digitalData is defined in the script
 */
if (process.client) {
  window.digitalData = window.digitalData || []
}


export default function useTracking (appContext) {
  const app = appContext || useContext()

  const filteringMethods = {}
  const { isDevEnv, isProdEnv } = useEnvironment()

  /**
   * Track data
   * @param {Object} data
   * @example push({
   *  "event":"Page Load",
   *  "data":{
   *    "pageName": "Oversigt",
   *    ...
   *  }
   * })
   */
  const push = (data) => {
    if (process.client) {
      window.digitalData.push(data)
      if (isDevEnv()) {
        console.log('tracking: ', digitalData[digitalData.length - 1].data);
      }
    }
  }



  const getInstallment = (installment) => {
    if (!installment) { return ''; }
    return installment.months > 0 ? 'Afbetaling i ' + installment.months + ' mdr.' : installment.displayText
  }


  const carouselArrowClick = ({ i, dir, productDetails, variantDetails, categorySlug, chosenChannel, chosenInstallment }) => {
    const details = productDetails || { value: variantDetails }
    const attributes = getEcProductsAttrs(details, chosenInstallment, chosenChannel, categorySlug)

    Object.keys(attributes).forEach(key => {
      if (!attributes[key]) {
        delete attributes[key]
      }
    })

    push({
      event: 'Link',
      data: {
        customEvent: ['ecProductImageArrow'],
        interactionLabel: 'image' + (i + 1),
        interactionComponent: dir + ' arrow',
        ecProducts: [attributes]
      }
    })
  }


  const loadProductListPage = ({ category }) => {
    push({
      event: 'Page Load',
      data: {
        pageName: 'Oversigt',
        pageType: 'product list',
        siteSections: ['shop', category],
        platform: 'web',
        userLoginStatus: 'Ikke logget ind', // or 'Logget ind' for logged in users
        userPermissions: []
      // @todo add tracking for logged in user
      // userId: '964452494', // set this for logged in users
      // userProductHolding: 'PAYG29', // set this for logged in users
      // userType: 'ROLE_NORMAL_USER' // set this for logged in users
      }
    })
  }

  const toggleListPageAccordion = ({ panel, i, panelModel }) => {
    const isOpen = !panelModel.includes(i)
    push({
      event: 'Link',
      data: {
        customEvent: [isOpen ? 'accordionOpen' : 'accordionClose'],
        interactionLabel: panel.heading, // faq title
        interactionComponent: 'FAQ'
      }
    })
  }

  const toggleDetailsPageAccordion = toggleListPageAccordion

  const toggleFilterLevel1 = ({ title, panelModel, i }) => {
    const isOpen = panelModel.includes(i)
    push({
      event: 'Link',
      data: {
        customEvent: [isOpen ? 'ecProductFilterListClose' : 'ecProductFilterListOpen'],
        interactionLabel: title, // or ’Passer til’ etc.
        interactionComponent: 'filter'
      }
    })
  }

  const filtersClearAll = () => {
    push({
      event: 'Link',
      data: {
        customEvent: ['ecProductFilterClearAll'],
        filterResults: '',
        filteringMethods: {},
        interactionLabel: 'clear all',
        interactionComponent: 'filter'
      }
    })
  }



  // eslint-disable-next-line complexity
  const filterChanged = ({ filterName, bucket, name, event }) => {
    const selected = event.includes(bucket.id) || event.includes(name) || event.includes(bucket.key) || name // these are different in accessories/devices
    const { itemSearchResult } = useSearch(app)
    const label = selected ? 'selection' : 'de-selection'
    let selectedFilters = {}

    if (selected) {
      filteringMethods[filterName] = filteringMethods[filterName] || new Set()
      filteringMethods[filterName].add(name)

      selectedFilters = Object.keys(filteringMethods).reduce((prev, curr) => {
        prev[curr] = Array.from(filteringMethods[curr])
        return prev
      }, {})
    } else {
      filteringMethods[filterName]?.delete(name)
      selectedFilters[filterName] = [name]
    }

    push({
      event: 'Link',
      data: {
        customEvent: ['ecProductFilterChanged'],
        filterResults: itemSearchResult.value.pagination.total,
        filteringMethods: selectedFilters,
        interactionLabel: 'filter ' + label,
        interactionComponent: 'filter'
      }
    })
  }

  const getChannelPriceForProductAttrs = (isCash, chosenChannel) => {
    if (isCash) {
      return chosenChannel?.value?.productBundlePrice?.cashPrice;
    } else {
      return chosenChannel?.value?.productBundlePrice?.productWithSubscriptionPrice;
    }
  }

  const getMasterVariantPriceForProductAttrs = (productDetails) => {
    if (productDetails?.value?.masterVariantPricingDetails && productDetails?.value?.masterVariantPricingDetails?.value?.installmentPrice) {
      return productDetails?.value?.masterVariantPricingDetails?.value?.installmentPrice;
    } else if (productDetails?.value?.masterVariantPricingDetails) {
      return productDetails?.value?.masterVariantPricingDetails?.value?.productWithSubscriptionPrice;
    }
  }

  const getPriceForProductAttrs = (masterVariantPrice, channelPrice, chosenVariantCashPrice, productDetails) => {
    if (masterVariantPrice) {
      return masterVariantPrice;
    } else if (productDetails.value.multiPropProdCategory && channelPrice) {
      return channelPrice;
    } else {
      return chosenVariantCashPrice;
    }
  }


  const getPaymentTypeForProductAttrs = (isCash, chosenInstallment) => {
    if (isCash) {
      return 'Kontant';
    } else if (typeof chosenInstallment === 'string') {
      return chosenInstallment;
    } else {
      return getInstallment(chosenInstallment?.value || null);
    }
  }


  const getEcProductsAttrs = (productDetails, chosenInstallment, chosenChannel, categorySlug, getCategorySlugById) => {
    const isCash = chosenChannel && chosenChannel?.value?.channelKey === 'Cash';
    const channelPrice = getChannelPriceForProductAttrs(isCash, chosenChannel)

    const chosenVariantCashPrice = productDetails.value.chosenVariantCashPrice;
    const masterVariantPrice = getMasterVariantPriceForProductAttrs(productDetails)

    const price = getPriceForProductAttrs(masterVariantPrice, channelPrice, chosenVariantCashPrice, productDetails)

    const categories = productDetails?.value?.chosenVariant?.categoryIDs?.map(id => {
      return getCategorySlugById && getCategorySlugById(id)
    }).filter(item => item).join(', ')

    const type = productDetails?.value?.chosenVariant?.productTypeKey
    const basePrice = productDetails?.value?.chosenVariant?.basePrice

    return {
      action: 'view',
      available: unref(productDetails.value.stockCheck) ? 'yes' : 'no',
      brand: unref(productDetails.value.productBrand),
      category: categories || categorySlug,
      paymentType: getPaymentTypeForProductAttrs(isCash, chosenInstallment),
      name: unref(productDetails.value.productName),
      price,
      cashPrice: chosenChannel?.value?.productBundlePrice?.cashPrice,
      basePrice,
      bundlePrice: chosenChannel?.value?.productBundlePrice?.bundlePrice,
      discount: chosenChannel?.value?.productBundlePrice?.discount,
      minimumPrice: chosenChannel?.value?.productBundlePrice?.minimumPrice,
      type,
      quantity: 1, // number of units in the basket. Default should be 1
      sku: unref(productDetails.value.chosenVariantSku),
      togetherWith: [chosenChannel?.value?.subscription?.displayName || ''], // Cross-link for Device and Plan respectively in the Device + Plan purchase case. Set the value to blank if not applicable
      variant: { color: unref(productDetails.value.activeColorName), size: unref(productDetails.value.activeStorage) }
    };
  };

  /**
   * Product Details
   */
  const loadProductsDetailsPage = ({ productDetails, chosenInstallment, chosenChannel, categorySlug, getCategorySlugById }) => {
    const ecProductsAttrs = getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel, categorySlug, getCategorySlugById)

    push({
      event: 'Page Load',
      data: {
        pageName: productDetails.value.productName,
        pageType: 'product detail',
        siteSections: ['shop', categorySlug],
        platform: 'web',
        userLoginStatus: 'Ikke logget ind', // or 'Logget ind'
        customEvent: ['ecProductView'],
        ecProducts: [ecProductsAttrs]
      }
    })
  }

  const changeColor = ({ color, productDetails, chosenInstallment, chosenChannel }) => {
    const ecProductsAttrs = getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel)
    push({
      event: 'Link',
      data: {
        customEvent: ['hardwareChange'],
        interactionLabel: color, // Name of the color changed to
        interactionComponent: 'color change',
        ecProducts: [ecProductsAttrs]
      }
    })
  }

  const changeStorage = ({ storage, productDetails, chosenInstallment, chosenChannel, categorySlug }) => {
    push({
      event: 'Link',
      data: {
        customEvent: ['hardwareChange'],
        interactionLabel: storage, // Name of the color changed to
        interactionComponent: 'storage change',
        ecProducts: [getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel, categorySlug)]
      }
    })
  }



  const changeInstallment = ({ installment, close, label, productDetails, chosenChannel, categorySlug }) => {
    if (!installment) { return; }
    push({
      event: 'Link',
      data: {
        customEvent: [close ? 'paymentTypeChangeClose' : 'paymentTypeChange'],
        interactionLabel: label,
        interactionComponent: 'payment type change',
        ecProducts: [getEcProductsAttrs(productDetails, installment, chosenChannel, categorySlug)]
      }
    })
  }

  const startSubscriptionChangeOnMitTelmore = () => {
    push({
      event: 'Link',
      data: {
        customEvent: ['ecCheckout'],
        interactionLabel: 'Skift Telmore-abonnement',
        interactionComponent: 'startSubscriptionChangeOnMitTelmore'
      }
    })
  }

  const changeSubscription = ({ chosenInstallment, channel, subscriptionName, close, productDetails, categorySlug }) => {
    if (!subscriptionName) { return; }

    push({
      event: 'Link',
      data: {
        customEvent: [close ? 'subscriptionChangeClose' : 'subscriptionChange'],
        interactionLabel: subscriptionName, // Change to subscription name
        interactionComponent: 'subscription change',
        ecProducts: [getEcProductsAttrs(productDetails, chosenInstallment, channel, categorySlug)]
      }
    })
  }
  const familyConcept = ({ interactionLabel, interactionComponent }) => {
    push({
      event: 'Link',
      data: {
        customEvent: 'familierabat',
        interactionLabel,
        interactionComponent
      }
    })
  }
  const familyConceptBuy = ({ interactionLabel, interactionComponent, parentSubscription, childSubscriptions }) => {
    push({
      event: 'Link',
      data: {
        customEvent: 'familierabat',
        interactionLabel,
        interactionComponent,
        ecProducts: [{
          available: 'yes',
          category: 'subscriptions, familieabonnement',
          lineItemPrice: parentSubscription?.cartProduct?.additionalBillingInformation?.hasDiscountPeriod
            ? parentSubscription?.cartProduct?.additionalBillingInformation?.recurringDiscountPayment.chargeValue
            : parentSubscription?.cartProduct?.additionalBillingInformation?.recurringPayment.chargeValue,
          name: parentSubscription?.cartProduct?.additionalBillingInformation?.displayName,
          price: parentSubscription?.cartProduct?.additionalBillingInformation?.hasDiscountPeriod
            ? parentSubscription?.cartProduct?.additionalBillingInformation?.recurringDiscountPayment.chargeValue
            : parentSubscription?.cartProduct?.additionalBillingInformation?.recurringPayment.chargeValue,
          quantity: 1,
          sku: parentSubscription.sku,
          togetherWith: childSubscriptions,
          type: 'mobilesubscription'
        }]
      }
    })
  }
  const keepSubscription = ({ interactionLabel }) => {
    push({
      event: 'Link',
      data: {
        customEvent: 'keep subscription',
        interactionLabel,
        interactionComponent: 'keep subscription'
      }
    })
  }
  const keepSubscriptionErrors = ({ errorMessage, errorType, errorCode }) => {
    push({
      event: 'Link',
      data: {
        customEvent: 'keep subscription',
        errorMessage,
        errorType,
        errorCode,
        interactionComponent: 'customerConfirmationType'
      }
    })
  }
  const keepSubscriptionModal = ({ close }) => {
    push({
      event: 'Link',
      data: {
        customEvent: [close ? 'keepCurrentSubscriptionModalClose' : 'keepCurrentSubscriptionModalOpen'],
        interactionLabel: 'keep current subscription modal ' + (close ? 'close' : 'open'),
        interactionComponent: 'keep current subscription'
      }
    })
  }

  const addToBasket = ({ productDetails, chosenInstallment, chosenChannel, categorySlug, label }) => {
    push({
      event: 'Link',
      data: {
        customEvent: ['ecProductBuyButtonClick'],
        interactionLabel: label,
        interactionComponent: 'buy button click',
        ecProducts: [getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel, categorySlug)]
      }
    })
  }

  const minimumPrice = ({ productDetails, chosenInstallment, chosenChannel }) => {
    push({
      event: 'Link',
      data: {
        customEvent: ['ecProductMinimumPriceClick'],
        interactionLabel: 'Mindstepris i 6 måneder',
        interactionComponent: 'minimum price link click',
        ecProducts: [getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel)]
      }
    })
  }

  const applyVoucher = ({ voucherCode, productDetails, chosenInstallment, chosenChannel, categorySlug, discountAmount, minimumPrice }) => {
    push({
      event: 'Link',
      data: {
        customEvent: ['voucherApplied'],
        interactionLabel: voucherCode,
        interactionComponent: 'voucher code apply',
        ecProducts: [getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel, categorySlug)],
        voucherCode,
        ...(discountAmount && { discountAmount }),
        ...(minimumPrice && { minimumPrice })
      }
    })
  }

  const applyVoucherError = ({ voucherCode, errorMessage, errorCode, productDetails, chosenInstallment, chosenChannel, categorySlug }) => {
    push({
      event: 'Validation',
      data: {
        customEvent: ['voucherApplyError'],
        interactionComponent: 'voucher code apply',
        errorString: errorMessage,
        errorCode,
        voucherCode,
        ecProducts: [getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel, categorySlug)]
      }
    })
  }

  const removeVoucher = ({ voucherCode, productDetails, chosenInstallment, chosenChannel, categorySlug }) => {
    push({
      event: 'Link',
      data: {
        customEvent: ['voucherRemoved'],
        interactionLabel: voucherCode,
        interactionComponent: 'voucher code remove',
        ecProducts: [getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel, categorySlug)]
      }
    })
  }

  const reapplyVoucher = ({ voucherCode, reason, productDetails, chosenInstallment, chosenChannel, categorySlug }) => {
    push({
      event: 'Link',
      data: {
        customEvent: ['voucherReapplied'],
        interactionLabel: voucherCode,
        interactionComponent: 'voucher code reapply',
        interactionReason: reason, // e.g., 'color change', 'storage change', 'channel change', 'installment change'
        ecProducts: [getEcProductsAttrs(productDetails, chosenInstallment, chosenChannel, categorySlug)]
      }
    })
  }

  const getCartProductsAttributes = (orderData = false) => {
    const { cart, getCategorySlugById, checkoutType, shoppingList, activeShoppingListItem, parentChildSubscriptionsInShoppingList } = getCheckoutTrackingContext()
    const data = orderData || cart.value
    const trackingSnapshot = data?.trackingSnapshot

    const getProdAttrCategories = (cartItem) => {
      return cartItem?.cartProduct?.categoryIDs?.map(id => {
        return getCategorySlugById && getCategorySlugById(id)
      }).filter(item => item).join(', ')
    }


    const getPaymentType = (payment) => payment ? `Afbetaling i ${payment?.months} måneder. ${payment?.amount}` : 'cash';

    // eslint-disable-next-line complexity
    const getTogetherWith = (cartItems, index) => {
      if (shoppingList.value?.shoppingListItem && shoppingList.value?.shoppingListItem?.length > 1) {
        let togetherWithProduct = []
        const activeItemIndex = shoppingList.value?.shoppingListItem.indexOf(activeShoppingListItem.value)
        if (activeItemIndex === 0) {
          togetherWithProduct = parentChildSubscriptionsInShoppingList?.value?.childSubscriptions
        } else if (parentChildSubscriptionsInShoppingList?.value?.childSubscriptions.length > 1) {
          togetherWithProduct = parentChildSubscriptionsInShoppingList?.value?.childSubscriptions?.splice(activeItemIndex, 1)
          togetherWithProduct.push(parentChildSubscriptionsInShoppingList?.value?.parentSubscription)
        } else {
          togetherWithProduct.push(parentChildSubscriptionsInShoppingList?.value?.parentSubscription)
        }
        return togetherWithProduct
      } else if (cartItems.length <= 1) { return []; }
      const togetherWithProduct = cartItems[index > 0 ? 0 : 1]?.cartProduct;
      return togetherWithProduct ? [`${togetherWithProduct?.name} - sku: ${togetherWithProduct?.sku}`] : [];
    };

    const getImageUrl = (image) => image ? `https:${image}?q=100&w=320&h=320` : '';

    // eslint-disable-next-line complexity
    const getOptionalProperties = (discount, lineItemPrice, minimumPrice, selectedInstallment, minimumPayment, basePrice, playMixItems) => ({
      ...(discount && { discount }),
      ...(lineItemPrice && { lineItemPrice }),
      ...(minimumPrice && { minimumPrice }),
      ...(selectedInstallment && { selectedInstallment }),
      ...(minimumPayment && { minimumPayment }),
      ...(basePrice && { basePrice }),
      ...(playMixItems && { playMixItems })
    });

    // eslint-disable-next-line complexity
    const getAttrs = (cartItem, index, orderData, cartItems) => {
      const payment = cartItem?.cartProduct?.selectedPrice?.selectedInstallment;
      const paymentType = getPaymentType(payment);
      const togetherWith = getTogetherWith(cartItems, index);
      const basePrice = cartItem?.cartProduct?.selectedPrice?.basePrice;
      const discount = cartItem?.cartProduct?.selectedPrice?.discount;
      const lineItemPrice = cartItem?.cartProduct?.selectedPrice?.lineItemPrice;
      const minimumPrice = cartItem?.cartProduct?.selectedPrice?.minimumPrice;
      const selectedInstallment = cartItem?.cartProduct?.selectedPrice?.selectedInstallment;
      const minimumPayment = cartItem?.additionalBillingInformation?.minimumPayment;
      const price = lineItemPrice || basePrice || '';

      const playMixItems = cartItem?.itemAttributes?.find(item => item.name === 'PlayPoList')?.value
      const image = cartItem?.cartProduct?.productContent?.productImages?.at(0).imageUrl
      // const imageUrl = `https:${image}?q=100&w=320&h=320` // dimentions for terminal mail
      let additionalSiteSections = []
      const productType = cartItem?.productType
      const bundleType = cartItem?.cartProduct?.additionalBillingInformation?.bundleType

      if (productType === 'MOBILE_BROADBAND_SUBSCRIPTION') {
        additionalSiteSections = ['mbb']
      }

      const effectiveCheckoutType = trackingSnapshot?.checkoutType &&
        trackingSnapshot.checkoutType !== CHECKOUT_FLOW.NO_FLOW
        ? trackingSnapshot.checkoutType
        : (orderData ? resolveCheckoutTypeFromCart(data) : checkoutType?.value)
      if (effectiveCheckoutType === CHECKOUT_FLOW.WITH_MV_SUBSCRIPTION_ONLY) {
        if (productType === 'SUBSCRIPTION' && bundleType === 'MEDIA') {
          additionalSiteSections = [...additionalSiteSections, 'play']
        } else {
          additionalSiteSections = [...additionalSiteSections, 'mobilevoice']
        }
      }
      if (trackingSnapshot?.productAddOnLabel) {
        additionalSiteSections = [...additionalSiteSections, trackingSnapshot.productAddOnLabel]
      } else if (shoppingList.value?.shoppingListItem && shoppingList.value?.shoppingListItem?.length > 1) {
        const activeItemIndex = shoppingList.value?.shoppingListItem.indexOf(activeShoppingListItem.value)
        const activeIndexText = 'ProductAddOn ' + (activeItemIndex + 1) + ' out of ' + shoppingList.value?.shoppingListItem.length
        additionalSiteSections = [...additionalSiteSections, activeIndexText]
      } else {
        additionalSiteSections = [...additionalSiteSections, 'No ProductAddOn']
      }


      return {
        action: 'view',
        available: orderData ? 'yes' : cartItem?.cartProduct?.isOnStock ? 'yes' : 'no',
        brand: cartItem?.cartProduct?.brandName,
        category: getProdAttrCategories(cartItem),
        name: cartItem?.cartProduct?.name,
        paymentType,
        quantity: cartItem?.quantity || 1,
        sku: cartItem?.sku || '',
        togetherWith,
        type: cartItem?.cartProduct?.productTypeKey || '',
        variant: { color: cartItem?.cartProduct?.colorDescription || '', size: cartItem?.cartProduct?.storage || '' },
        price,
        additionalSiteSections,
        ...(image && { imageUrl: getImageUrl(image) }),
        ...getOptionalProperties(discount, lineItemPrice, minimumPrice, selectedInstallment, minimumPayment, basePrice, playMixItems)
      };
    }

    return data?.cartItemGroupList?.flatMap(groupItem => {
      const cartItems = groupItem.cartItems
      return cartItems.map((cartItem, index) => getAttrs(cartItem, index, orderData, cartItems))
    })
  }

  /**
   * Checkout
   */
  const loadPageCheckout = () => {
    const productAttrs = getCartProductsAttributes()
    const siteSection = getSiteSection(productAttrs)
    const MitIdErrorString = getMitIdErrorString()

    push({
      event: 'Page Load',
      data: {
        pageName: 'Shop Kundeoplysninger',
        pageType: 'checkout',
        platform: 'web',
        siteSections: ['shop', 'checkout', ...siteSection],
        userLoginStatus: 'Ikke logget ind',
        ecFlowCategory: 'Shop',
        ecStepName: 'Kundeoplysninger',
        ecStepNumber: '1',
        ecCustomerType: 'Non-customer',
        customEvent: ['ecBeginCheckout'],
        ecProducts: productAttrs,
        ...(MitIdErrorString && { MitIdErrorString })
      }
    })
  }

  const errorMsg = ({ msg, interactionComponent = '' }) => {
    push({
      event: 'Validation',
      data: {
        customEvent: ['validation'],
        ecFlowCategory: 'Shop',
        ...(interactionComponent && { interactionComponent }),
        errorString: msg.join ? msg.join(' ') : msg
      }
    })
  }

  const menuChange = ({ label, currentStep, trackingEvent, trackingStepName }) => {
    const isSubscriptionStep = trackingEvent === 'subscriptionOptionSelected'
    push({
      event: 'Link',
      data: {
        customEvent: [trackingEvent],
        ecFlowCategory: 'Shop',
        ecStepName: trackingStepName,
        ecStepNumber: '' + (currentStep.value + 1),
        ecCustomerType: 'Non-customer',
        interactionLabel: label,
        interactionComponent: isSubscriptionStep ? 'setting of subscription at checkout' : 'customer type click'
      }
    })
  }

  const getCustomerData = customer => {
    const checkoutTrackingContext = getCheckoutTrackingContext()

    const firstName = customer?.firstName
    const companyName = customer?.company
    const lastName = customer?.lastName
    const email = checkoutTrackingContext?.cart?.value?.billingAddress?.email
    const phone = checkoutTrackingContext?.cart?.value?.billingAddress?.phone
    const cvr = checkoutTrackingContext?.state?.customerInfo?.businessInfo?.cvr
    const cpr = checkoutTrackingContext?.state?.customerInfo?.privateInfo?.cpr

    return {
      userName: firstName,
      userSurname: lastName,
      userEmail: email,
      userPhoneNumber: phone,
      useCompanyName: companyName,
      cvr,
      cpr
    }
  }


  const getCategorySlugFromProductAttrs = (productAttrs) => {
    const categoryStr = productAttrs?.find(item => item.category)?.category
    return getSpecificCategorySlug(categoryStr)
  }

  const getSectionFromCategoryScenario = (orderData = null) => {
    const snapshotSlug = orderData?.trackingSnapshot?.categorySlug
    if (snapshotSlug) {
      return snapshotSlug
    }

    const { getAttrFromCartScenario, getCategoryForCartItemGroup, getCategorySlugById, cart } = getCheckoutTrackingContext()
    const cat = getCategoryForCartItemGroup()
    const scenarioCategoryId = getAttrFromCartScenario(cart?.value, 'categoryId')
    const section = getCategorySlugById(cat || scenarioCategoryId)
    if (section) {
      return section
    }

    if (orderData) {
      const orderCategoryId = getAttrFromCartScenario(orderData, 'categoryId')
      return getCategorySlugById(orderCategoryId)
    }

    return null
  }

  const getSiteSection = (productAttrs, orderData = null) => {
    const rawSection = getSectionFromCategoryScenario(orderData) || getCategorySlugFromProductAttrs(productAttrs)
    const additionalSiteSections = [...new Set(productAttrs?.flatMap(item => item.additionalSiteSections) || [])]
    const section = resolvePrimarySiteSection(rawSection, additionalSiteSections)
    const remainingAdditional = additionalSiteSections.filter(slug => slug !== section)
    return [section, ...remainingAdditional].filter(e => e)
  }

  const getMitIdErrorString = () => {
    if (!process.client) { return '' }
    const searchParams = new URLSearchParams(location?.search)
    return searchParams.get('error')
  }

  const checkoutStepLoad = ({ currentStep, name }) => {
    const { state, cart, subscriptionList, getItemAttributeFromCartItem } = getCheckoutTrackingContext()
    const selectedMenu = state?.customerInfo?.menu?.value
    const b2cOrNot = selectedMenu === 'private' ? 'B2C' : undefined;
    const b2corb2b = selectedMenu === 'business' ? 'B2B' : b2cOrNot

    const customer = cart?.value?.billingAddress
    const customerData = getCustomerData(customer)
    const permissionsAccepted = getItemAttributeFromCartItem(subscriptionList?.value, 'PermissionEmail') === 'true'

    const productAttrs = getCartProductsAttributes()
    const siteSection = getSiteSection(productAttrs)
    const MitIdErrorString = getMitIdErrorString()

    push({
      event: 'Page Load',
      data: {
        pageName: 'Shop ' + name,
        pageType: 'checkout',
        platform: 'web',
        siteSections: ['shop', 'checkout', ...siteSection],
        userLoginStatus: 'Ikke logget ind',
        userPermissions: permissionsAccepted,
        ...customerData,
        ecFlowCategory: 'Shop',
        ecStepName: name,
        ecStepNumber: currentStep && '' + (currentStep?.value + 1),
        ecCustomerType: 'Non-customer',
        customEvent: ['ecCheckout'],
        ecCustomerSegment: b2corb2b,
        ecProducts: productAttrs,
        ...(MitIdErrorString && { MitIdErrorString })
      }
    })
  }


  const receiptLoadPage = ({ order, paymentMethod }) => {
    if (order.hasErrors) { return; }

    const customer = order?.billingAddress
    const customerData = getCustomerData(customer)
    const ecCustomerSegment = customer?.company ? 'B2B' : 'B2C'

    let permissionsAccepted = false

    const productAttrs = getCartProductsAttributes(order)
    const siteSection = getSiteSection(productAttrs, order)
    const ecOrderId = order.orderCompletedId || app.$storage?.getCookie?.('orderID') || ''

    const setPerm = attr => {
      if (attr.name === 'PermissionEmail' && attr.value === 'true') {
        permissionsAccepted = true
      }
    }

    order?.cartItemGroupList?.forEach(listItem => {
      listItem?.cartItems?.forEach(item => {
        item?.itemAttributes?.forEach(setPerm)
      })
    })

    push({
      event: 'Page Load',
      data: {
        ecCustomerSegment,
        pageName: 'Kvittering',
        pageType: 'kvittering',
        platform: 'web',
        siteSections: ['shop', 'checkout', ...siteSection],
        userLoginStatus: 'Ikke logget ind', // or "Logget ind”
        ...customerData,
        ecFlowCategory: 'Shop',
        ecStepName: 'Kvittering',
        ecCustomerType: 'Non-customer',
        ecPaymentMethod: paymentMethod, // Mobile Pay or Express Bank
        ecOrderId,
        ecOrderTotal: order.totalCartPriceInKR,
        customEvent: ['ecPurchase'],
        userPermissions: permissionsAccepted,
        ecProducts: productAttrs
      }
    })
  }


  const fieldError = ({ errorMessage, fieldName }) => {
    push({
      event: 'Link',
      data: {
        customEvent: ['formFieldError'],
        errorMessage: ['' + fieldName + ' - ' + errorMessage] // e.g. ”email - invalid email address”
      }
    })
  }

  const trackFieldRule = ({ message }) => {
    // fieldError({ errorMessage: message, fieldName })
    return message
  }

  const trackFieldError = ({ event, ctx, fieldName = 'unnamed field' }) => {
    if (event) {
      fieldError({
        errorMessage: ctx.errorBucket[0] || Array.from(ctx.errorMessages || ctx.$parent.errors || []).join(', '),
        fieldName
      })
    }
  }

  const playMixSelectorLoaded = ({ subscription }) => {
    push({
      event: 'Page Load',
      data: {
        customEvent: 'PlayWithAds - Pre Step 1 - MV PLAY',
        pageName: 'PlayWithAds - Pre Step 1 - MV PLAY',
        ecProducts: [{
          available: 'yes',
          category: 'subscriptions',
          lineItemPrice: subscription?.hasDiscountPeriod
            ? subscription?.recurringDiscountPayment.chargeValue
            : subscription?.recurringPayment.chargeValue,
          name: subscription?.displayName,
          price: subscription?.hasDiscountPeriod
            ? subscription?.recurringDiscountPayment.chargeValue
            : subscription?.recurringPayment.chargeValue,
          quantity: 1,
          sku: subscription?.bundleName,
          togetherWith: [],
          type: 'mobilesubscription'
        }]
      }
    })
  }

  const playMixSelectorServiceSelection = ({ subscription, service, deSelected, selectedPlayServices }) => {
    push({
      event: 'Link',
      data: {
        customEvent: 'PlayWithAds - Pre Step 1 - MV PLAY',
        interactionLabel: service.name,
        interactionComponent: deSelected ? 'Substract' : 'Add',
        ecProducts: [{
          available: 'yes',
          category: 'subscriptions',
          lineItemPrice: subscription?.hasDiscountPeriod
            ? subscription?.recurringDiscountPayment.chargeValue
            : subscription?.recurringPayment.chargeValue,
          name: subscription?.displayName,
          playMixItems: selectedPlayServices,
          price: subscription?.hasDiscountPeriod
            ? subscription?.recurringDiscountPayment.chargeValue
            : subscription?.recurringPayment.chargeValue,
          quantity: 1,
          sku: subscription?.bundleName,
          togetherWith: [],
          type: 'mobilesubscription'
        }]
      }
    })
  }

  const playMixSelectorServicesAddBasket = ({ subscription, selectedPlayServices, buttonText }) => {
    push({
      event: 'Link',
      data: {
        customEvent: 'PlayWithAds - Pre Step 1 - MV PLAY',
        interactionLabel: buttonText,
        interactionComponent: 'Button',
        ecProducts: [{
          available: 'yes',
          category: 'subscriptions',
          lineItemPrice: subscription?.hasDiscountPeriod
            ? subscription?.recurringDiscountPayment.chargeValue
            : subscription?.recurringPayment.chargeValue,
          name: subscription?.displayName,
          playMixItems: selectedPlayServices,
          price: subscription?.hasDiscountPeriod
            ? subscription?.recurringDiscountPayment.chargeValue
            : subscription?.recurringPayment.chargeValue,
          quantity: 1,
          sku: subscription?.bundleName,
          togetherWith: [],
          type: 'mobilesubscription'
        }]
      }
    })
  }

  const goToExpressBank = () => {
    push({
      event: 'Link',
      data: {
        customEvent: ['startLoanApplication'],
        interactionLabel: 'Gå til Resurs Bank',
        interactionComponent: 'loan application',
        ecProducts: getCartProductsAttributes()
      }
    })
  }


  const siteSection = ({ pageTitle, sectionName, step = null, category = '' }) => {
    push({
      event: 'Page Load',
      data: {
        customEvent: 'ecProductView',
        eventName: 'ecProductView',
        ecProducts: [{ category }],
        pageName: pageTitle,
        siteSections: ['shop', sectionName],
        ecStepNumber: step,
        platform: 'web',
        userLoginStatus: 'Ikke logget ind'
      }
    })
  }

  const pageLoad = ({ pageName }, ...rest) => {
    push({
      event: 'Page Load',
      data: {
        pageType: 'Side',
        siteSection: 'shop',
        pageName,
        ...rest
      }
    })
  }


  const getCheckoutTrackingContext = () => {
    try {
      const { cart, getCategoryForCartItemGroup, getItemAttributeFromCartItem, subscriptionList } = useTlmCart(app)
      const { state, checkoutType } = useCheckout(app)
      const { getScenario, getAttrFromCartScenario } = useCartScenarios(app);
      const { getCategorySlugById } = useCategories(app.$hubble)
      const { currentStep } = useFlow(app, state)
      const { shoppingList, activeShoppingListItem, parentChildSubscriptionsInShoppingList } = useShoppingList(app)
      return {
        parentChildSubscriptionsInShoppingList,
        activeShoppingListItem,
        shoppingList,
        getScenario,
        getAttrFromCartScenario,
        cart,
        getItemAttributeFromCartItem,
        subscriptionList,
        app,
        state,
        checkoutType,
        currentStep,
        getCategorySlugById,
        getCategoryForCartItemGroup
      }
    } catch (error) {
      console.warn(error)
      return {}
    }
  }

  const tracking = {
    familyConceptBuy,
    familyConcept,
    errorMsg,
    pageLoad,
    siteSection,
    goToExpressBank,
    keepSubscription,
    keepSubscriptionErrors,
    keepSubscriptionModal,
    fieldError,
    receiptLoadPage,
    menuChange,
    loadPageCheckout,
    minimumPrice,
    addToBasket,
    changeSubscription,
    changeInstallment,
    changeStorage,
    changeColor,
    loadProductsDetailsPage,
    filterChanged,
    push,
    carouselArrowClick,
    filtersClearAll,
    toggleFilterLevel1,
    loadProductListPage,
    toggleListPageAccordion,
    checkoutStepLoad,
    toggleDetailsPageAccordion,
    startSubscriptionChangeOnMitTelmore,
    playMixSelectorLoaded,
    playMixSelectorServiceSelection,
    playMixSelectorServicesAddBasket,
    applyVoucher,
    applyVoucherError,
    removeVoucher,
    reapplyVoucher
  }

  const track = (name, data) => {
    try {
      tracking[name](data)
    } catch (error) {
      if (!isProdEnv() && process.client) {
        throw new Error(`(Update tracking when changing properties etc.) Tracking error, ${name}: ` + error)
      } else {
        console.warn(`useTracking.js: Tracking error, ${name}:`, error)
      }
    }
  }

  return { track, trackFieldRule, trackFieldError }
}
